/**
 * Stock Check View Component
 * 
 * Displays stock status, expiration warnings, and recommendations before list generation
 */

import React from 'react';

const StockCheckView = ({ stockCheckData, onGenerateList, onCancel }) => {
    if (!stockCheckData || stockCheckData.length === 0) {
        return (
            <div className="bg-neutral-0 p-6 hairline">
                <p className="font-sans text-base text-neutral-900">No ingredients to check.</p>
            </div>
        );
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case 'expired':
                return <span className="text-red-600">❌</span>;
            case 'expiring_soon':
                return <span className="text-yellow-600">⚠️</span>;
            default:
                return <span className="text-green-600">✅</span>;
        }
    };

    const getStatusColor = (status, inStock) => {
        if (status === 'expired') return 'border-red-600 bg-red-50';
        if (status === 'expiring_soon') return 'border-yellow-600 bg-yellow-50';
        if (inStock) return 'border-green-600 bg-green-50';
        return 'border-neutral-200 bg-neutral-0';
    };

    return (
        <div className="bg-neutral-0 p-6 hairline">
            <div className="mb-6">
                <h2 className="font-sans text-3xl font-medium text-[#1b263b] mb-2">
                    Stock Check
                </h2>
                <p className="font-sans text-base text-neutral-900 opacity-60">
                    Review your kitchen stock and adjust quantities as needed
                </p>
            </div>

            <div className="space-y-3 mb-6">
                {stockCheckData.map((item, index) => (
                    <div
                        key={index}
                        className={`p-4 hairline ${getStatusColor(item.expiration_status, item.in_stock)}`}
                    >
                        <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    {getStatusIcon(item.expiration_status || (item.in_stock ? 'in_stock' : 'not_in_stock'))}
                                    <h3 className="font-sans text-xl font-medium text-neutral-900">
                                        {item.ingredient_name}
                                    </h3>
                                </div>
                                
                                {item.in_stock && (
                                    <p className="font-sans text-sm text-neutral-900 opacity-80 mb-1">
                                        In Stock: {item.quantity_available} {item.unit || ''}
                                    </p>
                                )}
                                
                                {item.expiration_warning && (
                                    <p className={`font-sans text-sm mb-1 ${
                                        item.expiration_status === 'expired' ? 'text-red-600' : 'text-yellow-600'
                                    }`}>
                                        {item.expiration_warning}
                                    </p>
                                )}
                                
                                <p className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60">
                                    Need: {item.quantity_needed} {item.unit || ''} | {item.recommendation}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex gap-4">
                <button
                    onClick={onGenerateList}
                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none"
                    style={{ backgroundColor: '#1b263b', color: '#FFFFFF' }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.6'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                    Generate List
                </button>
                <button
                    onClick={onCancel}
                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

export default StockCheckView;

